/**
 * Background location for session travel / kilometre capture.
 *
 * ONE position stream for the whole Sessions feature. The km tracker
 * (`active-session-store` via `WebLocationTracker`) and the en-route journey
 * hook both consume {@link watchSessionPosition}; what feeds it depends on the
 * platform and on whether the native service is running:
 *
 *  - NATIVE + service running (Android location foreground service / iOS Core
 *    Location with background updates — plugin "BackgroundLocation"): fixes are
 *    captured natively while the phone is locked or Maps is in front, buffered
 *    on-device, and DRAINED here in timestamp order — live on every "fix" poke
 *    while the WebView is awake, and on resume / start for everything that
 *    accumulated while JS was suspended. The WebView watcher is OFF meanwhile.
 *  - Otherwise (web, plugin missing, permission refused, service refused):
 *    the classic `navigator.geolocation.watchPosition` — the pre-existing
 *    foreground-only behaviour.
 *
 * Every fix keeps its OWN device timestamp and accuracy; nothing is re-stamped.
 * Exact duplicates (same timestamp + coordinates — e.g. a web fix and a native
 * fix overlapping around a source hand-over, or a buffered fix re-read after a
 * torn drain) are dropped once. Nothing else is filtered here: sanity rules
 * (implausible speed, time gaps, jitter) stay in `shared/sessions/km.ts` and
 * the journey math, which every consumer already runs.
 *
 * LIFECYCLE is owned by the active-session store, not by subscribers:
 * {@link syncBackgroundLocation} is called with the desired state (travel
 * started → arrival as `travel`, check-in → check-out as `session`, otherwise
 * `null`) and this module starts / re-targets / stops the native service to
 * match. Never always-on: no active session ⇒ the service is stopped (also at
 * app start, which clears an orphan left by a process kill).
 *
 * LIVE PINGS while backgrounded: with JS suspended nothing here can ping, so
 * {@link armNativeLivePing} lets the native layer post en-route journey pings
 * itself (only while the app is not in the foreground) — see
 * `features/sessions/hooks/use-native-live-ping.ts`.
 */

import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'
import type { GeoStamp } from '@shared/sessions/types'
import { isNativeApp } from '../utils/platform'
import { forwardNativeLivePingTelemetry } from './live-ping-telemetry'

export type BackgroundLocationReason = 'travel' | 'session'

export interface BackgroundLocationTarget {
  sessionId: string
  reason: BackgroundLocationReason
  /** Ongoing-notification copy (Android). Must stay privacy-safe: no member, no address. */
  title?: string
  body?: string
}

interface NativeFix {
  lat: number
  lng: number
  timestamp: number
  accuracy?: number
  speedMps?: number
  isMock?: boolean
  /** Session the native service was tracking when it captured this fix. */
  sessionId?: string
}

interface NativeStatus {
  running: boolean
  sessionId?: string | null
  reason?: string | null
  bufferedCount?: number
  livePingArmed?: boolean
}

/**
 * What the native layer needs to post live en-route pings itself while the
 * app is backgrounded (WebView JS suspended). `idToken` is a short-lived
 * Cognito ID token — never a refresh token; native keeps it in memory only.
 */
export interface NativeLivePingConfig {
  sessionId: string
  apiBaseUrl: string
  idToken: string
  /** Token expiry, epoch ms — native stops live posting before it. */
  expiresAt: number
  /**
   * Armed before the Sessions API confirmed the journey start (the travel
   * tap): native treats "journey inactive" replies as "not started yet" for a
   * short grace window instead of disarming.
   */
  awaitingServerStart?: boolean
}

/** What the travel tap hands native (the session id comes from the target). */
export type NativeLivePingTapCredentials = Pick<NativeLivePingConfig, 'apiBaseUrl' | 'idToken' | 'expiresAt'>

export interface BackgroundLocationPluginApi {
  start(options: {
    sessionId: string
    reason: BackgroundLocationReason
    title?: string
    body?: string
    /**
     * Travel TAP only: arm native live pings in the same call (JS cannot run
     * to do it once Maps takes the screen). Token in native memory only.
     */
    apiBaseUrl?: string
    idToken?: string
    expiresAt?: number
    /** Android 13+: don't ask for POST_NOTIFICATIONS again (already asked once). */
    skipNotificationPrompt?: boolean
  }): Promise<{ running: boolean; sessionId?: string }>
  stop(): Promise<{ fixes: NativeFix[] }>
  /** Drain buffered fixes; with `sessionId`, fixes tagged for other sessions are discarded natively. */
  drainBuffered(options?: { sessionId?: string }): Promise<{ fixes: NativeFix[] }>
  getStatus(): Promise<NativeStatus>
  /** iOS (after build 98): the Precise Location switch (`CLLocationManager.accuracyAuthorization`). */
  getAccuracyAuthorization(): Promise<{ accuracy: string }>
  /**
   * `etaMinutes`: the live server ETA the native service renders into the
   * ongoing notification title ("On the way · ~N min away"); `-1` clears it,
   * absent leaves whatever native last showed (it also updates the figure
   * itself from its own ping responses while the WebView is backgrounded).
   */
  updateNotification(options: { title: string; body: string; etaMinutes?: number }): Promise<void>
  /** Arm / refresh native live journey pings (token held in native memory only). */
  setLivePing(options: NativeLivePingConfig): Promise<{ armed: boolean }>
  /** Drop the native live-ping credentials. */
  clearLivePing(): Promise<void>
  /**
   * Pull + clear the native live-ping telemetry queued while JS was suspended
   * (codes/counts only). Optional in the type only: Capacitor's plugin proxy
   * exposes EVERY method name as a function, so on an older native shell the
   * call rejects ("not implemented") rather than being undefined —
   * `drainLivePingTelemetry` relies on its try/catch for that case.
   */
  drainTelemetry?(): Promise<{ events: unknown[] }>
  addListener(
    eventName: 'fix',
    listener: (data: { count: number }) => void,
  ): Promise<PluginListenerHandle>
  addListener(
    eventName: 'stateChange',
    listener: (data: { running: boolean; sessionId?: string; error?: string }) => void,
  ): Promise<PluginListenerHandle>
  addListener(
    eventName: 'notificationTap',
    listener: (data: { sessionId: string }) => void,
  ): Promise<PluginListenerHandle>
}

const PLUGIN_NAME = 'BackgroundLocation'

let plugin: BackgroundLocationPluginApi = registerPlugin<BackgroundLocationPluginApi>(PLUGIN_NAME)

/** Web watcher options — unchanged from the pre-native tracker. */
const WEB_WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 30_000,
  maximumAge: 1_000,
}

/** Exact-duplicate window: enough for a long buffered drain plus live overlap. */
const SEEN_KEYS_MAX = 4_000

/* ------------------------------ availability ------------------------------ */

let availabilityOverride: boolean | null = null

/** True when the native plugin is compiled into this shell. */
export function isBackgroundLocationAvailable(): boolean {
  if (availabilityOverride !== null) return availabilityOverride
  try {
    return isNativeApp() && Capacitor.isPluginAvailable(PLUGIN_NAME)
  } catch {
    return false
  }
}

/** True when the consumer can expect ANY position stream on this device. */
export function isPositionWatchSupported(): boolean {
  if (isBackgroundLocationAvailable()) return true
  return typeof navigator !== 'undefined' && Boolean(navigator.geolocation)
}

/* ------------------------------ the stream -------------------------------- */

type FixListener = (fix: GeoStamp) => void
type ErrorListener = (error: GeolocationPositionError) => void

interface Subscriber {
  onFix: FixListener
  onError?: ErrorListener
}

const subscribers = new Set<Subscriber>()
let webWatchId: number | null = null

/** The native service is running (our belief; corrected by stateChange events). */
let nativeActive = false
let nativeTarget: BackgroundLocationTarget | null = null

const seenKeys = new Map<string, true>()

function fixKey(fix: GeoStamp): string {
  return `${fix.timestamp}|${fix.lat.toFixed(6)}|${fix.lng.toFixed(6)}`
}

function rememberKey(key: string): void {
  seenKeys.set(key, true)
  if (seenKeys.size > SEEN_KEYS_MAX) {
    // Map preserves insertion order — evict the oldest.
    const oldest = seenKeys.keys().next().value
    if (oldest !== undefined) seenKeys.delete(oldest)
  }
}

function deliver(fix: GeoStamp): boolean {
  if (!Number.isFinite(fix.lat) || !Number.isFinite(fix.lng) || !Number.isFinite(fix.timestamp)) {
    return false
  }
  const key = fixKey(fix)
  if (seenKeys.has(key)) return false
  rememberKey(key)
  for (const subscriber of subscribers) {
    try {
      subscriber.onFix(fix)
    } catch {
      /* one consumer's failure must not starve the others */
    }
  }
  return true
}

function deliverError(error: GeolocationPositionError): void {
  for (const subscriber of subscribers) {
    try {
      subscriber.onError?.(error)
    } catch {
      /* ignore */
    }
  }
}

function toGeoStamp(fix: NativeFix): GeoStamp {
  return {
    lat: fix.lat,
    lng: fix.lng,
    timestamp: fix.timestamp,
    accuracy: typeof fix.accuracy === 'number' && Number.isFinite(fix.accuracy) ? fix.accuracy : undefined,
    speedMps:
      typeof fix.speedMps === 'number' && Number.isFinite(fix.speedMps) && fix.speedMps >= 0
        ? fix.speedMps
        : undefined,
    ...(fix.isMock ? { isMock: true } : {}),
  }
}

/** Map a browser position to the shared GeoStamp (same rule as location-provider). */
function geoStampFromBrowserPosition(position: GeolocationPosition): GeoStamp {
  const { latitude, longitude, accuracy, speed } = position.coords
  return {
    lat: latitude,
    lng: longitude,
    timestamp: position.timestamp || Date.now(),
    accuracy: typeof accuracy === 'number' ? accuracy : undefined,
    speedMps: typeof speed === 'number' && speed >= 0 ? speed : undefined,
  }
}

function startWebWatch(): void {
  if (webWatchId !== null) return
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  webWatchId = navigator.geolocation.watchPosition(
    (position) => {
      // A native fix for the same instant wins; the web copy is the duplicate.
      if (nativeActive) return
      deliver(geoStampFromBrowserPosition(position))
    },
    (error) => {
      if (nativeActive) return
      deliverError(error)
    },
    WEB_WATCH_OPTIONS,
  )
}

function stopWebWatch(): void {
  if (webWatchId === null) return
  try {
    navigator.geolocation.clearWatch(webWatchId)
  } catch {
    /* ignore */
  }
  webWatchId = null
}

/** Pick the source for the current state: native when running, else the web watcher. */
function ensureSources(): void {
  if (subscribers.size === 0) {
    stopWebWatch()
    return
  }
  if (nativeActive) {
    stopWebWatch()
    return
  }
  startWebWatch()
}

/**
 * Subscribe to the session position stream. Returns an unsubscribe function.
 * Subscribing never starts or stops the native service (see
 * {@link syncBackgroundLocation}); it only decides whether the web watcher
 * runs. When the native service is running, any fixes it has buffered are
 * drained to the new subscriber straight away.
 */
export function watchSessionPosition(onFix: FixListener, onError?: ErrorListener): () => void {
  const subscriber: Subscriber = { onFix, onError }
  subscribers.add(subscriber)
  ensureSources()
  if (nativeActive) void drainBufferedPositions()
  return () => {
    subscribers.delete(subscriber)
    ensureSources()
  }
}

/* ------------------------------ native bridge ----------------------------- */

let listenersReady: Promise<void> | null = null
let drainChain: Promise<number> = Promise.resolve(0)

type ActiveListener = (active: boolean) => void
const activeListeners = new Set<ActiveListener>()

function setNativeActive(active: boolean): void {
  if (nativeActive === active) return
  nativeActive = active
  if (!active) nativeTarget = null
  ensureSources()
  for (const listener of activeListeners) {
    try {
      listener(active)
    } catch {
      /* ignore */
    }
  }
}

/** Observe the native service running / not running (notification ownership). */
export function onBackgroundLocationActiveChange(listener: ActiveListener): () => void {
  activeListeners.add(listener)
  return () => activeListeners.delete(listener)
}

/** True while the native service is believed to be running. */
export function isBackgroundLocationActive(): boolean {
  return nativeActive
}

/** What the native service is currently tracking (null when not running). */
export function getBackgroundLocationTarget(): BackgroundLocationTarget | null {
  return nativeActive ? nativeTarget : null
}

async function ensureNativeListeners(): Promise<void> {
  if (listenersReady) return listenersReady
  listenersReady = (async () => {
    try {
      await plugin.addListener('fix', () => {
        void drainBufferedPositions()
      })
      await plugin.addListener('stateChange', ({ running, sessionId, error }) => {
        // The service is authoritative for the session WE asked it to track:
        // a refused foreground start or a permission revoked mid-run flips us
        // back to the web watcher. Events about another session (a retained
        // event replayed on attach, or the tail of a service we already
        // re-targeted) are stale and ignored.
        if (sessionId && nativeTarget && sessionId !== nativeTarget.sessionId) return
        if (!nativeTarget) return
        if (running) {
          setNativeActive(true)
          return
        }
        if (error === 'MAX_RUNTIME') {
          // The service capped its own runtime (14 h safety stop). Never
          // restart it for this session — the web watcher carries on.
          maxRuntimeSessionId = sessionId ?? nativeTarget.sessionId
        } else if (error === 'PERMISSION_DENIED') {
          rememberPermissionDenied()
        }
        setNativeActive(false)
      })
    } catch {
      /* plugin missing — availability check should have caught this */
    }
    // Everything captured while JS was suspended: pull it as soon as we're
    // back. And if a start was refused while backgrounded (Android 12+ only
    // allows a foreground service to start from the foreground), retry now.
    const onForeground = () => {
      void drainBufferedPositions()
      // Live-ping events the native pinger queued while JS was suspended.
      void drainLivePingTelemetry()
      if (desiredTarget && !nativeActive) void syncBackgroundLocation(desiredTarget)
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') onForeground()
      })
    }
    try {
      const { App } = await import('@capacitor/app')
      await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) onForeground()
      })
    } catch {
      /* @capacitor/app unavailable — visibilitychange still covers it */
    }
  })()
  return listenersReady
}

/**
 * Pull every natively buffered fix and deliver it to subscribers in timestamp
 * order. Drains are serialised so a "fix" poke, a resume, and an explicit call
 * can never interleave or reorder deliveries. Resolves with the number of NEW
 * fixes delivered. With no subscriber the buffer is left alone (nothing would
 * receive the fixes — they wait for the next subscriber).
 */
export function drainBufferedPositions(): Promise<number> {
  if (!isBackgroundLocationAvailable()) return Promise.resolve(0)
  if (subscribers.size === 0) return Promise.resolve(0)
  const run = async (): Promise<number> => {
    if (subscribers.size === 0) return 0
    const expected = currentSessionId()
    let fixes: NativeFix[]
    try {
      ;({ fixes } = await plugin.drainBuffered(expected ? { sessionId: expected } : undefined))
    } catch {
      return 0
    }
    return deliverNativeFixes(fixes, expected)
  }
  const next = drainChain.then(run, run)
  drainChain = next
  return next
}

/** The session whose fixes consumers currently expect (desired first, else what native tracks). */
function currentSessionId(): string | null {
  return desiredTarget?.sessionId ?? nativeTarget?.sessionId ?? null
}

/**
 * Deliver native fixes in timestamp order. Fixes tagged with a DIFFERENT
 * session than `expectedSessionId` are dropped: the native buffer is
 * session-scoped too, but a buffer left by a previous session (app killed
 * mid-visit, new session started) must never leak into this one's km or
 * journey. Untagged fixes (older native builds) pass through.
 */
function deliverNativeFixes(fixes: NativeFix[] | undefined, expectedSessionId: string | null): number {
  if (!Array.isArray(fixes) || fixes.length === 0) return 0
  const ordered = fixes
    .filter((fix) => !expectedSessionId || !fix.sessionId || fix.sessionId === expectedSessionId)
    .map(toGeoStamp)
    .filter((fix) => Number.isFinite(fix.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp)
  let delivered = 0
  for (const fix of ordered) {
    if (deliver(fix)) delivered += 1
  }
  return delivered
}

/* ------------------------------- lifecycle -------------------------------- */

let lifecycleChain: Promise<void> = Promise.resolve()
let desiredTarget: BackgroundLocationTarget | null = null
/** Session for which the service stopped itself at the runtime cap — never auto-restart it. */
let maxRuntimeSessionId: string | null = null
/* Denial memory: a user who refused location is not re-prompted on every
 * phase change / reload — only an explicit user action (start travel, record
 * arrival) asks again. Persisted so it survives a reload. Same for the one
 * Android 13+ notification prompt: asked once, then `skipNotificationPrompt`. */
const DENIED_KEY = 'ascend-bg-location-denied'
const NOTIF_ASKED_KEY = 'ascend-bg-location-notif-asked'

function readFlag(key: string): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return
    if (on) localStorage.setItem(key, '1')
    else localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function rememberPermissionDenied(): void {
  writeFlag(DENIED_KEY, true)
}

/** Drop denial / notification-prompt memory so a later sign-in can be asked again. */
export function clearBackgroundLocationPromptMemory(): void {
  writeFlag(DENIED_KEY, false)
  writeFlag(NOTIF_ASKED_KEY, false)
}

/** True when the user previously refused location and no explicit retry is pending. */
export function isBackgroundLocationPermissionDenied(): boolean {
  return readFlag(DENIED_KEY)
}

function sameTarget(a: BackgroundLocationTarget | null, b: BackgroundLocationTarget | null): boolean {
  if (!a || !b) return a === b
  return a.sessionId === b.sessionId && a.reason === b.reason
}

function errorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null
  const code = (err as { code?: unknown }).code
  return typeof code === 'string' ? code : null
}

async function applyDesired(
  userInitiated: boolean,
  livePing: NativeLivePingTapCredentials | null = null,
): Promise<void> {
  const target = desiredTarget
  if (!target) {
    const stoppingSession = nativeTarget?.sessionId ?? null
    if (!nativeActive) {
      // Also clears an orphaned service from a previous process (never
      // always-on): cheap no-op when nothing is running.
      try {
        const status = await plugin.getStatus()
        if (!status.running) return
      } catch {
        return
      }
    }
    try {
      const { fixes } = await plugin.stop()
      deliverNativeFixes(fixes, stoppingSession)
    } catch {
      /* best-effort */
    }
    setNativeActive(false)
    return
  }

  if (nativeActive && sameTarget(nativeTarget, target)) {
    if (target.title || target.body) void updateBackgroundLocationNotification(target)
    // Already tracking this travel (a retried tap): still hand native the
    // credentials. Not awaited — armNativeLivePing waits for this very chain.
    if (livePing && target.reason === 'travel') {
      void armNativeLivePing({ sessionId: target.sessionId, ...livePing, awaitingServerStart: true })
    }
    return
  }

  // Self-stopped at the runtime cap for this very session: don't start again.
  if (maxRuntimeSessionId != null && maxRuntimeSessionId === target.sessionId && !nativeActive) {
    return
  }

  // Remembered denial: only an explicit user action may prompt again.
  if (isBackgroundLocationPermissionDenied() && !userInitiated) return
  if (userInitiated) writeFlag(DENIED_KEY, false)

  // Register the listeners but do NOT wait for them: the start call must
  // reach the native bridge in the same JS turn as the tap (the WebView may
  // be suspended a moment later). stateChange is retained natively and the
  // buffered fixes are drained right after the start, so nothing is missed.
  const listeners = ensureNativeListeners()
  let skipNotificationPromptUsed = true
  try {
    const skipNotificationPrompt = readFlag(NOTIF_ASKED_KEY)
    skipNotificationPromptUsed = skipNotificationPrompt
    writeFlag(NOTIF_ASKED_KEY, true)
    const startOptions = {
      sessionId: target.sessionId,
      reason: target.reason,
      title: target.title,
      body: target.body,
      skipNotificationPrompt,
    }
    const tapLivePing = livePing && target.reason === 'travel' ? livePing : null
    const result = tapLivePing
      ? // The credentials ride the start call: keep them out of the bridge log.
        await withBridgeLoggingSuppressed(() => plugin.start({ ...startOptions, ...tapLivePing }))
      : await plugin.start(startOptions)
    nativeTarget = target
    if (result?.running !== false) setNativeActive(true)
    else setNativeActive(false)
  } catch (err) {
    // Permission refused / service refused: the web watcher carries on.
    if (errorCode(err) === 'PERMISSION_DENIED') rememberPermissionDenied()
    setNativeActive(false)
    return
  } finally {
    await listeners
  }
  // The first start may have asked for Android 13+ notifications: if that was
  // allowed, register for push now (never prompts) rather than at the next foreground.
  if (!skipNotificationPromptUsed) {
    void import('./push-registration').then((m) => m.registerForPush({ reason: 'location-start' })).catch(() => {})
  }
  // Surface the arm / refuse(reason) the native start just recorded.
  if (livePing && target.reason === 'travel') void drainLivePingTelemetry(target.sessionId)
  // Anything buffered before this JS instance attached (reload mid-session).
  void drainBufferedPositions()
}

export interface SyncBackgroundLocationOptions {
  /**
   * The call follows an explicit user action (started travel, recorded
   * arrival): a remembered permission denial may be re-prompted this once.
   */
  userInitiated?: boolean
  /**
   * The travel TAP: arm native live pings in the same native start call
   * (target.reason must be `travel`). Held only for this call — never stored.
   */
  livePing?: NativeLivePingTapCredentials | null
}

/**
 * Declare the desired native tracking state. Idempotent and serialised: call
 * it as often as the session state changes; the module reconciles.
 *
 *  - `{ sessionId, reason: 'travel' }` while a journey is en route or
 *    arrived (the dwell may complete with the phone locked),
 *  - `{ sessionId, reason: 'session' }` while checked in (km capture),
 *  - `null` when no session is active ⇒ stop.
 *
 * Re-targets are in place (never stop + restart). Resolves `true` when the
 * native service is running for `target` afterwards (always `false` on web /
 * without the plugin — callers keep using the web fallback through
 * {@link watchSessionPosition}).
 */
export function syncBackgroundLocation(
  target: BackgroundLocationTarget | null,
  options: SyncBackgroundLocationOptions = {},
): Promise<boolean> {
  desiredTarget = target
  if (maxRuntimeSessionId != null && target?.sessionId !== maxRuntimeSessionId) {
    maxRuntimeSessionId = null
  }
  if (!isBackgroundLocationAvailable()) return Promise.resolve(false)
  const userInitiated = options.userInitiated === true
  const livePing = options.livePing ?? null
  const run = () => applyDesired(userInitiated, livePing).catch(() => {})
  const next = lifecycleChain.then(run, run)
  lifecycleChain = next
  return next.then(() => nativeActive && sameTarget(nativeTarget, target))
}

/** Start tracking for a session (sugar over {@link syncBackgroundLocation}). */
export function startBackgroundLocation(target: BackgroundLocationTarget): Promise<boolean> {
  return syncBackgroundLocation(target)
}

/** Stop tracking (sugar over {@link syncBackgroundLocation}). */
export function stopBackgroundLocation(): Promise<void> {
  return syncBackgroundLocation(null).then(() => undefined)
}

/** Refresh the Android ongoing-notification copy (no-op elsewhere). Privacy-safe text only. */
/**
 * iOS Precise Location: `full` / `reduced`; `unknown` before location itself is
 * allowed; `unavailable` off iOS and on builds without the method (iOS 98 and
 * older) — setup then hides the step.
 */
export async function getLocationAccuracyAuthorization(): Promise<'full' | 'reduced' | 'unknown' | 'unavailable'> {
  if (Capacitor.getPlatform() !== 'ios') return 'unavailable'
  try {
    const { accuracy } = await plugin.getAccuracyAuthorization()
    if (accuracy === 'full' || accuracy === 'reduced' || accuracy === 'unknown') return accuracy
    return 'unavailable'
  } catch {
    return 'unavailable'
  }
}

export async function updateBackgroundLocationNotification(copy: {
  title?: string
  body?: string
  /** Live server ETA (minutes); `null` clears it; `undefined` leaves native's figure alone. */
  etaMinutes?: number | null
}): Promise<void> {
  if (!isBackgroundLocationAvailable() || !nativeActive) return
  if (!copy.title && !copy.body && copy.etaMinutes === undefined) return
  try {
    await plugin.updateNotification({
      title: copy.title ?? '',
      body: copy.body ?? '',
      ...(copy.etaMinutes !== undefined ? { etaMinutes: copy.etaMinutes ?? -1 } : {}),
    })
  } catch {
    /* best-effort */
  }
}

/* ------------------------- native live journey pings ------------------------ */

/**
 * Hand native the credentials to post live en-route journey pings while the
 * app is backgrounded (it posts only then — in the foreground this WebView's
 * own ping cadence owns live pings). Waits for any pending start / re-target
 * so the service is tracking `config.sessionId` for travel; resolves `false`
 * otherwise, on web, or on a native shell without the method. Call again to
 * refresh the token.
 */
export async function armNativeLivePing(config: NativeLivePingConfig): Promise<boolean> {
  if (!isBackgroundLocationAvailable()) return false
  await lifecycleChain
  if (!nativeActive || nativeTarget?.sessionId !== config.sessionId || nativeTarget.reason !== 'travel') {
    return false
  }
  try {
    const result = await withBridgeLoggingSuppressed(() => plugin.setLivePing(config))
    // Surface the arm / refuse(reason) the native layer just recorded.
    void drainLivePingTelemetry(config.sessionId)
    return result?.armed === true
  } catch {
    return false
  }
}

/**
 * Forward the native live-ping telemetry queue (arm / disarm(reason) /
 * refuse(reason) / post(status) / backoff / foreground flips / FGS start-stop /
 * decision summaries) into the call-telemetry pipeline. Best-effort, never
 * throws; resolves with how many events were forwarded.
 */
export async function drainLivePingTelemetry(sessionId?: string): Promise<number> {
  if (!isBackgroundLocationAvailable()) return 0
  try {
    // Old shells (no native `drainTelemetry`) reject here → caught below.
    const result = await plugin.drainTelemetry?.()
    return forwardNativeLivePingTelemetry(
      result?.events,
      sessionId ?? nativeTarget?.sessionId ?? null,
    )
  } catch {
    return 0
  }
}

let bridgeLoggingSuppressions = 0
let bridgeLoggingRestoreValue = false

/**
 * Capacitor's JS bridge logs every call's options to the console (logcat /
 * Xcode) in debug builds; the ID token must never be logged. The plugin proxy
 * dispatches asynchronously, so logging stays off until the call settles.
 */
async function withBridgeLoggingSuppressed<T>(call: () => Promise<T>): Promise<T> {
  const cap = (globalThis as { Capacitor?: { isLoggingEnabled?: boolean } }).Capacitor
  if (!cap) return call()
  if (bridgeLoggingSuppressions === 0) bridgeLoggingRestoreValue = cap.isLoggingEnabled === true
  bridgeLoggingSuppressions += 1
  cap.isLoggingEnabled = false
  try {
    return await call()
  } finally {
    bridgeLoggingSuppressions -= 1
    if (bridgeLoggingSuppressions === 0) cap.isLoggingEnabled = bridgeLoggingRestoreValue
  }
}

/** Drop native live-ping credentials (journey left en route, unmount, sign-out). Best-effort. */
export async function disarmNativeLivePing(): Promise<void> {
  if (!isBackgroundLocationAvailable()) return
  try {
    await plugin.clearLivePing()
    void drainLivePingTelemetry()
  } catch {
    /* older native shell without the method — nothing armed there */
  }
}

/**
 * Tap on the native ongoing notification → open the session. Resolves with a
 * remover; a no-op remover when the plugin is unavailable.
 */
export async function addBackgroundLocationNotificationTapListener(
  onTap: (sessionId: string) => void,
): Promise<() => void> {
  if (!isBackgroundLocationAvailable()) return () => {}
  try {
    const handle = await plugin.addListener('notificationTap', ({ sessionId }) => {
      if (sessionId) onTap(sessionId)
    })
    return () => void handle.remove()
  } catch {
    return () => {}
  }
}

/* --------------------------------- tests ---------------------------------- */

/** TEST ONLY: swap the native plugin (and force availability). */
export function __setBackgroundLocationPluginForTests(
  override: BackgroundLocationPluginApi | null,
  available: boolean | null = override ? true : null,
): void {
  plugin = override ?? registerPlugin<BackgroundLocationPluginApi>(PLUGIN_NAME)
  availabilityOverride = available
}

/** TEST ONLY: reset all module state. */
export function __resetBackgroundLocationForTests(): void {
  stopWebWatch()
  subscribers.clear()
  seenKeys.clear()
  activeListeners.clear()
  nativeActive = false
  nativeTarget = null
  desiredTarget = null
  maxRuntimeSessionId = null
  writeFlag(DENIED_KEY, false)
  writeFlag(NOTIF_ASKED_KEY, false)
  listenersReady = null
  drainChain = Promise.resolve(0)
  lifecycleChain = Promise.resolve()
}
